/**
 * Catering cart lock — prevents users from removing or editing catering line items
 * once added to cart via the configurator. Identified by SKU prefix "CAT-" or
 * vendor "Swee Choon Catering v2".
 *
 * - Intercepts fetch and XHR requests to /cart/change*, /cart/update*, /cart/change_all*
 *   and blocks any attempt to modify a locked line.
 * - Hides quantity controls and remove buttons in the ajax cart drawer.
 * - Server-side template also strips these controls on the /cart page.
 */
(function () {
  'use strict';

  var LOCK_STATE = {
    variantIds: new Set(),
    lineKeys: new Set(),
    linePositions: new Set(),
    ready: false
  };

  function isLockedSku(sku) {
    return typeof sku === 'string' && sku.indexOf('CAT-') === 0;
  }
  function isLockedVendor(vendor) {
    return vendor === 'Swee Choon Catering v2';
  }

  function refreshLockState() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var vs = new Set(), ks = new Set(), ps = new Set();
        (cart.items || []).forEach(function (item, idx) {
          if (isLockedSku(item.sku) || isLockedVendor(item.vendor)) {
            vs.add(String(item.variant_id));
            ks.add(String(item.key));
            ps.add(idx + 1); // Shopify line indices are 1-based
          }
        });
        LOCK_STATE.variantIds = vs;
        LOCK_STATE.lineKeys = ks;
        LOCK_STATE.linePositions = ps;
        LOCK_STATE.ready = true;
      })
      .catch(function () { /* ignore */ });
  }

  function warn() {
    try {
      // Silent by design (Ernest, 2026-08-23): locked lines already have their
      // qty/remove controls hidden and a banner above the cart explains why, so a
      // blocking alert() was pure noise. Blocking still happens — just quietly.
      console.warn('[catering-cart-lock] blocked a modification to a locked catering line');
    } catch (e) {}
  }

  function extractIdentifiers(url, body) {
    var out = { variant: null, key: null, line: null, updates: null };
    try {
      var u = url || '';
      var qIdx = u.indexOf('?');
      var params = new URLSearchParams(qIdx >= 0 ? u.slice(qIdx + 1) : '');
      var id = params.get('id');
      var line = params.get('line');
      if (id) out.variant = String(id);
      if (line) out.line = parseInt(line, 10);
    } catch (e) {}
    if (body) {
      try {
        if (typeof body === 'string') {
          if (body.charAt(0) === '{') {
            var j = JSON.parse(body);
            if (j.id != null) out.variant = String(j.id);
            if (j.line != null) out.line = parseInt(j.line, 10);
            if (j.updates) out.updates = j.updates;
          } else {
            var p = new URLSearchParams(body);
            if (p.get('id')) out.variant = String(p.get('id'));
            if (p.get('line')) out.line = parseInt(p.get('line'), 10);
            // Handle updates[key]=qty
            var updates = {};
            p.forEach(function (v, k) {
              var m = k.match(/^updates\[(.+)\]$/);
              if (m) updates[m[1]] = parseInt(v, 10);
            });
            if (Object.keys(updates).length) out.updates = updates;
          }
        } else if (body instanceof FormData) {
          var updatesF = {};
          body.forEach(function (v, k) {
            if (k === 'id') out.variant = String(v);
            if (k === 'line') out.line = parseInt(v, 10);
            var m = k.match(/^updates\[(.+)\]$/);
            if (m) updatesF[m[1]] = parseInt(v, 10);
          });
          if (Object.keys(updatesF).length) out.updates = updatesF;
        }
      } catch (e) {}
    }
    return out;
  }

  function shouldBlock(url, body, method) {
    if (!url) return false;
    // Explicit bypasses: the configurator may replace its own cart, and the cart page may
    // clear the complete order after the customer confirms. Per-line edits stay locked.
    if (window.__CATERING_CONFIG_BYPASS || window.__CATERING_CART_CLEAR_APPROVED) return false;
    if (!/\/cart\/(change|update|change_all|clear)(\.js)?(\?|$)/i.test(url)) return false;
    // Always block full clear if any catering items present
    if (/\/cart\/clear/i.test(url) && LOCK_STATE.lineKeys.size > 0) return true;
    var ids = extractIdentifiers(url, body);
    if (ids.variant && LOCK_STATE.variantIds.has(ids.variant)) return true;
    if (ids.line && LOCK_STATE.linePositions.has(ids.line)) {
      // Only block if the update tries to remove or change qty of a locked line
      return true;
    }
    if (ids.updates) {
      // updates map: keys can be line keys or line numbers
      for (var k in ids.updates) {
        var v = ids.updates[k];
        if (LOCK_STATE.lineKeys.has(String(k))) return true;
        // Numeric key = 1-based line position
        var n = parseInt(k, 10);
        if (!isNaN(n) && LOCK_STATE.linePositions.has(n)) return true;
      }
    }
    return false;
  }

  // Ensure lock state is fresh before checking
  var lockStatePromise = refreshLockState();

  // ---- Intercept fetch ----
  var _fetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    var body = init && init.body;
    var self = this;
    var check = function () {
      if (shouldBlock(url, body, method)) {
        warn();
        return Promise.reject(new Error('Catering line locked'));
      }
      var p = _fetch.call(self, input, init);
      // Filter search suggest responses to hide v2 catering products
      if (/\/search\/suggest(\.json)?/i.test(url)) {
        return p.then(function (res) {
          if (!res || !res.ok) return res;
          return res.clone().json().then(function (data) {
            try {
              var prods = data && data.resources && data.resources.results && data.resources.results.products;
              if (Array.isArray(prods)) {
                data.resources.results.products = prods.filter(function (p) {
                  var url = (p.url || '').toLowerCase();
                  // Filter obscure catering-config product handles by prefix
                  return !/\/products\/c-(buf|bkf|sem|pc|nd|rw|del|sur|sty|st)/.test(url);
                });
              }
            } catch (e) {}
            return new Response(JSON.stringify(data), { status: res.status, statusText: res.statusText, headers: res.headers });
          }).catch(function () { return res; });
        });
      }
      return p;
    };
    return LOCK_STATE.ready ? check() : lockStatePromise.then(check);
  };

  // ---- Intercept XHR ----
  var _open = XMLHttpRequest.prototype.open;
  var _send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cclUrl = url;
    this.__cclMethod = method;
    return _open.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    var url = this.__cclUrl;
    var run = function () {
      if (shouldBlock(url, body, this.__cclMethod)) {
        warn();
        // Fake a network error
        try { this.abort(); } catch (e) {}
        return;
      }
      return _send.call(this, body);
    }.bind(this);
    return LOCK_STATE.ready ? run() : lockStatePromise.then(run);
  };

  // ---- Refresh lock state after any successful cart mutation ----
  window.addEventListener('load', refreshLockState);
  document.addEventListener('cart.requestComplete', refreshLockState);

  // ---- Hide qty/remove controls in ajax cart drawer + also on /cart page for any missed rows ----
  function hideLockedControls(root) {
    root = root || document;
    var updates = root.querySelectorAll('input[name="updates[]"]');
    updates.forEach(function (input) {
      var key = input.getAttribute('data-id') || input.id?.replace('updates_', '');
      if (!key) return;
      if (LOCK_STATE.lineKeys.has(String(key))) {
        // Hide qty controls
        var wrap = input.closest('.js-qty__wrapper') || input.closest('.ajaxcart__quantity') || input.parentElement;
        if (wrap) wrap.style.display = 'none';
        // Hide remove links (no per-line locked label — a single banner sits above the cart)
        var host = input.closest('.ajaxcart__product') || input.closest('.cart__row');
        var removes = host ? host.querySelectorAll('a[href*="/cart/change"]') : [];
        removes.forEach(function (a) { a.style.display = 'none'; });
      }
    });
  }

  function scan() {
    if (!LOCK_STATE.ready) { lockStatePromise.then(function () { hideLockedControls(document); }); return; }
    hideLockedControls(document);
  }

  document.addEventListener('DOMContentLoaded', scan);
  document.addEventListener('cart.requestComplete', function () {
    refreshLockState().then(function () { hideLockedControls(document); });
  });

  // Observe cart drawer for re-renders
  var container = document.getElementById('CartContainer');
  if (container) {
    var mo = new MutationObserver(function () { scan(); });
    mo.observe(container, { childList: true, subtree: true });
  } else {
    // Container might not exist yet
    document.addEventListener('DOMContentLoaded', function () {
      var c = document.getElementById('CartContainer');
      if (c) {
        var mo2 = new MutationObserver(function () { scan(); });
        mo2.observe(c, { childList: true, subtree: true });
      }
    });
  }
})();
